(() => {
  if (window.__ntiBelleCodAssetLoaded) return;
  window.__ntiBelleCodAssetLoaded = true;

  const WORKER_URL =
    'https://nti-belle-cod-api.aymanehachadi16.workers.dev/api/create-cod-order';

  const COUPON_URL =
    'https://nti-belle-cod-api.aymanehachadi16.workers.dev/api/validate-coupon';

  const SITE_KEY = '0x4AAAAAAE7Gph_expVOfKoG';

  const turnstileWidgets = new WeakMap();
  const couponState = new WeakMap();

  // Instant UX preview only. The Worker remains authoritative.
  const INSTANT_COUPONS = {
    WELCOME10: { type: 'Pourcentage', value: 10 }
  };

  const getSubtotalCents = (root) => {
    const variants = getVariants(root);
    const variant = getSelectedVariant(root, variants);
    return variant ? (Number(variant.price) || 0) * qty(root) : 0;
  };

  const ensureCouponUI = (root) => {
    if (root.querySelector('[data-coupon-wrap]')) return;

    const commander = root.querySelector('[data-commander]');
    if (!commander) return;

    const ar = root.dataset.lang === 'ar';
    const wrap = document.createElement('div');
    wrap.setAttribute('data-coupon-wrap', '');
    wrap.style.margin = '12px 0';
    wrap.innerHTML = `
      <div style="display:flex;gap:8px">
        <input type="text" data-coupon-input maxlength="40" autocomplete="off"
          placeholder="${ar ? 'Code promo' : 'Code promo (optionnel)'}"
          style="min-width:0;flex:1;font-size:16px;padding:12px 14px;border:1px solid rgba(53,33,32,.18);border-radius:10px;background:#fff;color:#352120">
        <button type="button" data-coupon-apply
          style="padding:0 16px;border:1px solid #352120;border-radius:10px;background:#fff;color:#352120;font-weight:600;cursor:pointer">
          ${ar ? 'Appliquer' : 'Appliquer'}
        </button>
      </div>
      <div data-coupon-message hidden style="margin-top:7px;font-size:13px"></div>
    `;
    commander.parentNode.insertBefore(wrap, commander);
  };

  /*
   * Wait until Cloudflare Turnstile API is ready.
   */
  const waitForTurnstile = (timeout = 10000) =>
    new Promise((resolve, reject) => {
      if (window.turnstile) {
        resolve(window.turnstile);
        return;
      }

      const started = Date.now();

      const timer = window.setInterval(() => {
        if (window.turnstile) {
          window.clearInterval(timer);
          resolve(window.turnstile);
          return;
        }

        if (Date.now() - started >= timeout) {
          window.clearInterval(timer);
          reject(new Error('TURNSTILE_NOT_LOADED'));
        }
      }, 100);
    });

  /*
   * Render one Turnstile widget per COD block.
   */
  const ensureTurnstile = async (root) => {
    const container =
      root.querySelector('[data-turnstile-container]');

    if (!container) {
      throw new Error('TURNSTILE_CONTAINER_NOT_FOUND');
    }

    const turnstile = await waitForTurnstile();

    if (turnstileWidgets.has(root)) {
      return turnstileWidgets.get(root);
    }

    const widgetId = turnstile.render(container, {
      sitekey:
        container.dataset.sitekey ||
        SITE_KEY,

      theme: 'light'
    });

    turnstileWidgets.set(
      root,
      widgetId
    );

    return widgetId;
  };

  /*
   * Get current Turnstile token.
   */
  const getTurnstileToken = async (root) => {
    const turnstile =
      await waitForTurnstile();

    const widgetId =
      await ensureTurnstile(root);

    if (
      widgetId === null ||
      widgetId === undefined
    ) {
      return '';
    }

    return String(
      turnstile.getResponse(widgetId) || ''
    );
  };

  /*
   * Turnstile tokens are single-use.
   * Reset after a failed request.
   */
  const resetTurnstile = (root) => {
    if (!window.turnstile) return;

    const widgetId =
      turnstileWidgets.get(root);

    if (widgetId === undefined) return;

    try {
      window.turnstile.reset(widgetId);
    } catch (_) {}
  };

  /*
   * Product variants
   */
  const getVariants = (root) => {
    try {
      return JSON.parse(
        root.querySelector(
          '[data-variants]'
        )?.textContent || '[]'
      );
    } catch (_) {
      return [];
    }
  };

  /*
   * Currently selected Shopify variant
   */
  const getSelectedVariant = (
    root,
    variants
  ) => {
    const productInfo =
      root.closest(
        '.product-information'
      );

    const productForm =
      productInfo?.querySelector(
        'form[action*="/cart/add"]'
      ) ||
      document.querySelector(
        'form[action*="/cart/add"]'
      );

    const id = Number(
      productForm?.querySelector(
        '[name="id"]'
      )?.value || 0
    );

    return (
      variants.find(
        (x) =>
          Number(x.id) === id
      ) ||
      variants[0]
    );
  };

  /*
   * Quantity
   */
  const qty = (root) => {
    const input =
      root.querySelector(
        '[data-qty]'
      );

    return Math.max(
      1,
      Math.min(
        10,
        parseInt(
          input?.value || '1',
          10
        ) || 1
      )
    );
  };

  /*
   * Money formatting
   */
  const money = (
    root,
    cents
  ) => {
    try {
      return new Intl.NumberFormat(
        root.dataset.lang === 'ar'
          ? 'ar-MA'
          : 'fr-MA',
        {
          style: 'currency',
          currency:
            root.dataset.currency ||
            'MAD',
          minimumFractionDigits: 2
        }
      ).format(
        (Number(cents) || 0) /
          100
      );
    } catch (_) {
      return (
        (
          (Number(cents) || 0) /
          100
        ).toFixed(2) +
        ' dh'
      );
    }
  };

  /*
   * Sync product / quantity / totals
   */
  const sync = (root) => {
    const variants =
      getVariants(root);

    const variant =
      getSelectedVariant(
        root,
        variants
      );

    if (!variant) return;

    const n = qty(root);

    const q =
      root.querySelector(
        '[data-qty]'
      );

    if (q) {
      q.value = n;
    }

    const bubble =
      root.querySelector(
        '[data-qbubble]'
      );

    if (bubble) {
      bubble.textContent = n;
    }

    const unit =
      root.querySelector(
        '[data-unit]'
      );

    if (unit) {
      unit.textContent =
        money(
          root,
          variant.price
        );
    }

    const subtotal =
      root.querySelector(
        '[data-sub]'
      );

    if (subtotal) {
      subtotal.textContent =
        money(
          root,
          variant.price * n
        );
    }

    /*
     * Livraison gratuite.
     */
    const total =
      root.querySelector(
        '[data-total]'
      );

    if (total) {
      total.textContent =
        money(
          root,
          variant.price * n
        );
    }

    const title =
      root.querySelector(
        '[data-vtitle]'
      );

    if (title) {
      title.textContent =
        variant.title ===
        'Default Title'
          ? ''
          : variant.title;
    }

    const open =
      root.querySelector(
        '[data-open]'
      );

    if (open) {
      open.disabled =
        !variant.available;

      open.textContent =
        variant.available
          ? (
              root.dataset.lang ===
              'ar'
                ? 'اشتري الآن'
                : 'Acheter maintenant'
            )
          : (
              root.dataset.lang ===
              'ar'
                ? 'غير متوفر'
                : 'Indisponible'
            );
    }
  };

  /*
   * Coupon preview helpers
   */
  const setCouponMessage = (root, text, ok = false) => {
    const message = root.querySelector('[data-coupon-message]');
    if (!message) return;
    message.hidden = false;
    message.textContent = text;
    message.style.color = ok ? '#6f594e' : '#a83f46';
  };

  const clearCouponPreview = (root, clearInput = false) => {
    couponState.delete(root);

    const row = root.querySelector('[data-discount-row]');
    const discount = root.querySelector('[data-discount]');
    const total = root.querySelector('[data-total]');
    const input = root.querySelector('[data-coupon-input]');
    const message = root.querySelector('[data-coupon-message]');

    if (row) row.hidden = true;
    if (discount) discount.textContent = '';
    if (total) total.textContent = money(root, getSubtotalCents(root));
    if (clearInput && input) input.value = '';
    if (message) {
      message.hidden = true;
      message.textContent = '';
    }
  };

  const showCouponPreview = (root, coupon) => {
    const row = root.querySelector('[data-discount-row]');
    const discount = root.querySelector('[data-discount]');
    const total = root.querySelector('[data-total]');

    const subtotalMad = Number(coupon?.subtotal) || (getSubtotalCents(root) / 100);
    const finalMad = Math.round(Number(coupon?.total) || subtotalMad);
    const discountMad = Math.max(0, subtotalMad - finalMad);

    if (row) row.hidden = false;
    if (discount) discount.textContent = '-' + money(root, discountMad * 100);
    if (total) total.textContent = money(root, finalMad * 100);

    couponState.set(root, {
      code: String(coupon?.code || '').toUpperCase(),
      coupon,
      subtotalCents: getSubtotalCents(root)
    });

    setCouponMessage(
      root,
      root.dataset.lang === 'ar'
        ? 'تم تطبيق كود التخفيض ✓'
        : `Code ${String(coupon?.code || '').toUpperCase()} appliqué ✓`,
      true
    );
  };

  const invalidateCouponIfSubtotalChanged = (root) => {
    const state = couponState.get(root);
    if (!state) return;
    if (Number(state.subtotalCents) !== Number(getSubtotalCents(root))) {
      clearCouponPreview(root, false);
      setCouponMessage(
        root,
        root.dataset.lang === 'ar'
          ? 'أعيدي تطبيق كود التخفيض بعد تغيير الكمية.'
          : 'Réappliquez le code promo après avoir modifié la quantité.',
        false
      );
    }
  };

  /*
   * Errors
   */
  const showError = (
    root,
    message
  ) => {
    const error =
      root.querySelector(
        '[data-error]'
      );

    if (!error) return;

    error.textContent =
      message;

    error.hidden = false;
  };

  const hideError = (root) => {
    const error =
      root.querySelector(
        '[data-error]'
      );

    if (!error) return;

    error.hidden = true;
    error.textContent = '';
  };

  /*
   * Restore Commander button
   */
  const restoreCommander = (
    commander,
    ar
  ) => {
    commander.disabled =
      false;

    commander.textContent =
      ar
        ? 'تأكيد الطلب'
        : 'Commander';
  };

  /*
   * Main events
   */
  document.addEventListener(
    'click',
    async (event) => {

      /*
       * OPEN MODAL
       */
      const open =
        event.target.closest(
          '.nti-cod [data-open]'
        );

      if (open) {
        const root =
          open.closest(
            '.nti-cod'
          );

        sync(root);
        ensureCouponUI(root);
        hideError(root);

        const overlay =
          root.querySelector(
            '[data-overlay]'
          );

        if (overlay) {
          overlay.hidden =
            false;
        }

        document
          .documentElement
          .classList.add(
            'nti-cod-open'
          );

        /*
         * IMPORTANT:
         * Turnstile is rendered only
         * after modal is visible.
         *
         * It also waits for the
         * Cloudflare API if needed.
         */
        try {
          await ensureTurnstile(
            root
          );
        } catch (err) {
          console.error(
            '[Nti Belle COD] Turnstile render:',
            err
          );

          showError(
            root,
            root.dataset.lang ===
            'ar'
              ? 'تعذر تحميل التحقق الأمني. المرجو تحديث الصفحة.'
              : 'Impossible de charger la vérification de sécurité. Veuillez actualiser la page.'
          );
        }

        return;
      }

      /*
       * CLOSE MODAL
       */
      const close =
        event.target.closest(
          '.nti-cod [data-close]'
        );

      if (close) {
        const root =
          close.closest(
            '.nti-cod'
          );

        const overlay =
          root.querySelector(
            '[data-overlay]'
          );

        if (overlay) {
          overlay.hidden =
            true;
        }

        document
          .documentElement
          .classList.remove(
            'nti-cod-open'
          );

        return;
      }

      /*
       * QUANTITY - / +
       */
      const minus =
        event.target.closest(
          '.nti-cod [data-minus]'
        );

      const plus =
        event.target.closest(
          '.nti-cod [data-plus]'
        );

      if (minus || plus) {
        const root =
          (
            minus ||
            plus
          ).closest(
            '.nti-cod'
          );

        const input =
          root.querySelector(
            '[data-qty]'
          );

        if (input) {
          input.value =
            minus
              ? Math.max(
                  1,
                  qty(root) - 1
                )
              : Math.min(
                  10,
                  qty(root) + 1
                );
        }

        sync(root);
        invalidateCouponIfSubtotalChanged(root);

        return;
      }

      /*
       * APPLY COUPON
       */
      const couponApply =
        event.target.closest(
          '.nti-cod [data-coupon-apply]'
        );

      if (couponApply) {
        event.preventDefault();
        event.stopPropagation();

        const root = couponApply.closest('.nti-cod');
        const input = root.querySelector('[data-coupon-input]');
        const phoneInput = root.querySelector('[name="phone"]');

        const code = String(input?.value || '')
          .trim()
          .toUpperCase()
          .replace(/\s+/g, '');

        const phone = String(phoneInput?.value || '').trim();
        const subtotal = getSubtotalCents(root) / 100;

        clearCouponPreview(root, false);
        if (input) input.value = code;

        // Show a known coupon immediately, while the server validates it.
        const instantCoupon = INSTANT_COUPONS[code];
        if (instantCoupon && subtotal > 0) {
          const rawDiscount =
            instantCoupon.type === 'Pourcentage'
              ? subtotal * (Number(instantCoupon.value) || 0) / 100
              : Number(instantCoupon.value) || 0;

          const rawTotal = Math.max(0, subtotal - rawDiscount);
          const roundedTotal = Math.round(rawTotal);

          showCouponPreview(root, {
            code,
            type: instantCoupon.type,
            value: instantCoupon.value,
            subtotal,
            total: roundedTotal,
            discount: Math.max(0, subtotal - roundedTotal)
          });

          setCouponMessage(
            root,
            root.dataset.lang === 'ar'
              ? 'جار التحقق من كود التخفيض…'
              : 'Réduction appliquée — vérification…',
            true
          );
        }

        if (!code) {
          clearCouponPreview(root, false);
          setCouponMessage(
            root,
            root.dataset.lang === 'ar'
              ? 'أدخلي كود التخفيض.'
              : 'Saisissez un code promo.'
          );
          return;
        }

        if (!phone) {
          clearCouponPreview(root, false);
          setCouponMessage(
            root,
            root.dataset.lang === 'ar'
              ? 'أدخلي رقم الهاتف أولا.'
              : 'Saisissez d’abord votre téléphone.'
          );
          phoneInput?.focus();
          return;
        }

        couponApply.disabled = true;
        const oldText = couponApply.textContent;
        couponApply.textContent =
          root.dataset.lang === 'ar' ? '...' : 'Vérification…';

        const commanderDuringCoupon =
          root.querySelector('[data-commander]');
        const commanderWasDisabled =
          Boolean(commanderDuringCoupon?.disabled);

        if (commanderDuringCoupon) {
          commanderDuringCoupon.disabled = true;
        }

        try {
          const response = await fetch(COUPON_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              couponCode: code,
              phone,
              subtotal
            })
          });

          let result = null;
          try {
            result = await response.json();
          } catch (_) {
            throw new Error('INVALID_SERVER_RESPONSE');
          }

          if (!response.ok || !result?.ok || !result?.coupon) {
            clearCouponPreview(root, false);
            let message =
              root.dataset.lang === 'ar'
                ? 'كود التخفيض غير صالح أو غير متاح.'
                : 'Code promo invalide, expiré ou non applicable.';

            if (result?.error === 'INVALID_PHONE') {
              message =
                root.dataset.lang === 'ar'
                  ? 'أدخلي رقم هاتف مغربي صحيح.'
                  : 'Saisissez un numéro marocain valide.';
            } else if (result?.error === 'TOO_MANY_COUPON_ATTEMPTS') {
              message =
                root.dataset.lang === 'ar'
                  ? 'محاولات كثيرة. حاولي بعد لحظات.'
                  : 'Trop de tentatives. Réessayez dans quelques secondes.';
            }

            setCouponMessage(root, message, false);
            return;
          }

          showCouponPreview(root, result.coupon);

          const validatedState = couponState.get(root);
          if (validatedState) {
            validatedState.validated = true;
            couponState.set(root, validatedState);
          }
        } catch (err) {
          console.error('[Nti Belle COD] Coupon preview:', err);
          clearCouponPreview(root, false);
          setCouponMessage(
            root,
            root.dataset.lang === 'ar'
              ? 'تعذر التحقق من كود التخفيض. حاولي مرة أخرى.'
              : 'Impossible de vérifier le code promo. Veuillez réessayer.',
            false
          );
        } finally {
          couponApply.disabled = false;
          couponApply.textContent = oldText;

          if (commanderDuringCoupon && !commanderWasDisabled) {
            commanderDuringCoupon.disabled = false;
          }
        }

        return;
      }

      /*
       * COMMANDER
       */
      const commander =
        event.target.closest(
          '.nti-cod [data-commander]'
        );

      if (!commander) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      /*
       * Prevent double click.
       */
      if (
        commander.disabled
      ) {
        return;
      }

      const root =
        commander.closest(
          '.nti-cod'
        );

      const variants =
        getVariants(root);

      const variant =
        getSelectedVariant(
          root,
          variants
        );

      const n =
        qty(root);

      const ar =
        root.dataset.lang ===
        'ar';

      const name =
        String(
          root.querySelector(
            '[name="name"]'
          )?.value || ''
        ).trim();

      const phone =
        String(
          root.querySelector(
            '[name="phone"]'
          )?.value || ''
        ).trim();

      const address =
        String(
          root.querySelector(
            '[name="address"]'
          )?.value || ''
        ).trim();

      const city =
        String(
          root.querySelector(
            '[name="city"]'
          )?.value || ''
        ).trim();

      const activeCouponState =
        couponState.get(root);

      const couponCode =
        String(
          activeCouponState?.validated
            ? activeCouponState.code
            : ''
        )
          .trim()
          .toUpperCase();

      const subtotal =
        getSubtotalCents(root) / 100;

      /*
       * Form validation
       */
      if (
        !name ||
        !phone ||
        !address ||
        !city
      ) {
        showError(
          root,
          ar
            ? 'المرجو ملء جميع المعلومات المطلوبة.'
            : 'Veuillez remplir toutes les informations.'
        );

        return;
      }

      const phoneDigits =
        phone.replace(
          /\D/g,
          ''
        );

      if (
        phoneDigits.length <
          9 ||
        phoneDigits.length >
          15
      ) {
        showError(
          root,
          ar
            ? 'المرجو إدخال رقم هاتف صحيح.'
            : 'Veuillez saisir un numéro valide.'
        );

        return;
      }

      if (
        !variant ||
        !variant.available
      ) {
        showError(
          root,
          ar
            ? 'هذا المنتج غير متوفر حالياً.'
            : 'Ce produit est actuellement indisponible.'
        );

        return;
      }

      /*
       * TURNSTILE
       */
      let turnstileToken =
        '';

      try {
        turnstileToken =
          await getTurnstileToken(
            root
          );
      } catch (err) {
        console.error(
          '[Nti Belle COD] Turnstile:',
          err
        );
      }

      if (
        !turnstileToken
      ) {
        showError(
          root,
          ar
            ? 'المرجو إكمال التحقق الأمني.'
            : 'Veuillez terminer la vérification de sécurité.'
        );

        return;
      }

      hideError(root);

      /*
       * Lock Commander
       */
      commander.disabled =
        true;

      commander.textContent =
        ar
          ? 'جارٍ تأكيد الطلب…'
          : 'Confirmation de la commande…';

      try {
        /*
         * Send directly to Worker.
         *
         * NO CART.
         * NO CHECKOUT.
         */
        const response =
          await fetch(
            WORKER_URL,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json'
              },

              body:
                JSON.stringify({
                  variantId:
                    String(
                      variant.id
                    ),

                  quantity: n,

                  name,
                  phone,
                  address,
                  city,

                  couponCode,
                  subtotal,

                  turnstileToken
                })
            }
          );

        let result = null;

        try {
          result =
            await response.json();
        } catch (_) {
          throw new Error(
            'INVALID_SERVER_RESPONSE'
          );
        }

        /*
         * Worker / Shopify error
         */
        if (
          !response.ok ||
          !result?.ok
        ) {
          console.error(
            '[Nti Belle COD] Worker response:',
            result
          );

          let message =
            ar
              ? 'تعذر تسجيل الطلب. المرجو المحاولة مرة أخرى.'
              : 'Impossible d’enregistrer la commande. Veuillez réessayer.';

          if (
            result?.error ===
            'INVALID_PHONE'
          ) {
            message =
              ar
                ? 'المرجو إدخال رقم هاتف صحيح.'
                : 'Veuillez saisir un numéro valide.';
          }

          if (
            result?.error ===
            'MISSING_OR_INVALID_FIELDS'
          ) {
            message =
              ar
                ? 'المرجو التأكد من جميع المعلومات.'
                : 'Veuillez vérifier toutes les informations.';
          }

          if (
            result?.error ===
              'TURNSTILE_REQUIRED' ||
            result?.error ===
              'TURNSTILE_FAILED'
          ) {
            message =
              ar
                ? 'تعذر التحقق الأمني. المرجو إعادة المحاولة.'
                : 'La vérification de sécurité a expiré. Veuillez réessayer.';

            resetTurnstile(
              root
            );
          }

          if (
            String(result?.error || '').startsWith('COUPON_')
          ) {
            message =
              ar
                ? 'Code promo invalide, expire ou non applicable.'
                : 'Code promo invalide, expiré ou non applicable.';
          }

          if (
            result?.error ===
            'TOO_MANY_ORDERS'
          ) {
            message =
              ar
                ? 'Trop de tentatives. Reessayez dans quelques secondes.'
                : 'Trop de tentatives. Réessayez dans quelques secondes.';
          }

          showError(
            root,
            message
          );

          restoreCommander(
            commander,
            ar
          );

          return;
        }

        /*
         * ORDER CREATED
         */
        console.log(
          '[Nti Belle COD] Order created:',
          result.orderName
        );

        commander.textContent =
          ar
            ? 'تم تأكيد الطلب ✓'
            : 'Commande confirmée ✓';

        /*
         * Save temporary order data
         * for /pages/merci.
         *
         * No personal information
         * is placed in the URL.
         */
        try {
          sessionStorage.setItem(
            'ntiBelleLastOrder',
            JSON.stringify({
              orderName:
                result.orderName ||
                '',

              name,
              phone,
              address,
              city,

              product:
                root.dataset.title ||
                '',

              variant:
                variant.title ===
                'Default Title'
                  ? ''
                  : variant.title,

              quantity: n,

              total:
                result.total ||
                (
                  (
                    Number(
                      variant.price
                    ) || 0
                  ) *
                    n /
                  100
                ).toFixed(2),

              currency:
                result.currency ||
                root.dataset
                  .currency ||
                'MAD',

              payment:
                'Cash on Delivery (COD)',

              shipping:
                'Gratuite',

              couponCode:
                result.coupon?.code ||
                couponCode ||
                '',

              discount:
                result.coupon?.discount ||
                0
            })
          );
        } catch (err) {
          console.warn(
            '[Nti Belle COD] sessionStorage:',
            err
          );
        }

        /*
         * Redirect to Thank You page
         */
        const redirect =
          result.redirect ||
          '/pages/merci';

        const destination =
          new URL(
            redirect,
            window.location
              .origin
          );

        if (
          result.orderName
        ) {
          destination
            .searchParams
            .set(
              'order',
              result.orderName
            );
        }

        window.location.assign(
          destination.toString()
        );
      } catch (err) {
        console.error(
          '[Nti Belle COD]',
          err
        );

        /*
         * Token may have been consumed
         * or expired.
         */
        resetTurnstile(
          root
        );

        showError(
          root,
          ar
            ? 'وقع خطأ في الاتصال. المرجو المحاولة مرة أخرى.'
            : 'Une erreur de connexion est survenue. Veuillez réessayer.'
        );

        restoreCommander(
          commander,
          ar
        );
      }
    },
    true
  );

  /*
   * Close when clicking overlay
   */
  document.addEventListener(
    'click',
    (event) => {
      const overlay =
        event.target.closest(
          '.nti-cod [data-overlay]'
        );

      if (
        overlay &&
        event.target === overlay
      ) {
        overlay.hidden =
          true;

        document
          .documentElement
          .classList.remove(
            'nti-cod-open'
          );
      }
    }
  );

  /*
   * Editing a validated coupon invalidates its preview.
   */
  document.addEventListener(
    'input',
    (event) => {
      if (!event.target.matches('.nti-cod [data-coupon-input]')) return;

      const root = event.target.closest('.nti-cod');
      const state = couponState.get(root);
      if (!state) return;

      const current = String(event.target.value || '')
        .trim()
        .toUpperCase()
        .replace(/\s+/g, '');

      if (current !== state.code) {
        clearCouponPreview(root, false);
      }
    }
  );

  /*
   * Manual quantity input
   */
  document.addEventListener(
    'input',
    (event) => {
      if (
        !event.target.matches(
          '.nti-cod [data-qty]'
        )
      ) {
        return;
      }

      const root =
        event.target.closest(
          '.nti-cod'
        );

      sync(root);
      invalidateCouponIfSubtotalChanged(root);
    }
  );

  /*
   * Shopify variant change
   */
  document.addEventListener(
    'change',
    () => {
      document
        .querySelectorAll(
          '.nti-cod'
        )
        .forEach((root) => {
          sync(root);
          invalidateCouponIfSubtotalChanged(root);
        });
    }
  );

  /*
   * Initial sync
   */
  document
    .querySelectorAll(
      '.nti-cod'
    )
    .forEach(sync);
})();